import { useEffect, useState, useMemo } from 'react';
import { Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

const workerUrl = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;
if (pdfjsLib.GlobalWorkerOptions.workerSrc !== workerUrl) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
}

// Polygon component for rendering highlights
const HighlightPolygon = ({ coords, scale, style }) => (
    <polygon
        points={coords.reduce((acc, coord, i) => {
            if (i % 2 === 0) acc.push(`${coord * scale},${coords[i + 1] * scale}`);
            return acc;
        }, []).join(' ')}
        style={style}
    />
);

// Component for rendering search and line highlights
const AnnotationLayer = ({ scale, highlightCoords, searchCoords, selectedLine }) => (
    <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
        {/* Search highlight (yellow) */}
        {searchCoords.length > 0 && searchCoords.map((coord, index) => (
            <HighlightPolygon
                key={`search-highlight-${index}`}
                coords={coord}
                scale={scale}
                style={{ 
                    fill: 'rgba(255, 255, 0, 0.3)',
                    stroke: '#FFD700',
                    strokeWidth: 1,
                    zIndex: 2 
                }}
            />
        ))}
        
        {/* Line click highlight (red) */}
        {selectedLine !== null && highlightCoords.length > 0 && (
            <HighlightPolygon
                coords={highlightCoords}
                scale={scale}
                style={{ 
                    fill: 'rgba(255, 0, 0, 0.5)',
                    stroke: 'red',
                    strokeWidth: 2,
                    zIndex: 2 
                }}
            />
        )}
    </svg>
);

// Helper function to scale coordinates
const scaleCoordinates = (coords, widthScaleFactor, heightScaleFactor, isSearchCoords) => {
    if (isSearchCoords) {
        return coords.map((coord, index) =>
            coord.map((c, i) => i % 2 === 0 ? c * widthScaleFactor : c * heightScaleFactor)
        );
    } else {
        return coords.map((c, i) => i % 2 === 0 ? c * widthScaleFactor : c * heightScaleFactor)
    }
};

// Main PDF viewer component
export default function SimplePdfViewer({
    pdfUrl,
    highlightCoords,
    searchCoords,
    imageSize,
    selectedLine,
    onDocumentLoad,
    onDocumentLoadFail,
}) {
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: () => [],
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const {
                        ZoomIn,
                        ZoomOut,
                        SwitchTheme,
                        EnterFullScreen,
                        Print,
                        Download,
                    } = slots;

                    const isArabic =
                        typeof window !== 'undefined' &&
                        localStorage.getItem('lang') === 'ar';

                    return (
                        <div
                            className={`rpv-toolbar pdf-viewer-toolbar-wrapper ${
                                isArabic ? 'rpv-toolbar--rtl' : ''
                            }`}
                            role="toolbar"
                            aria-orientation="horizontal"
                        >
                            <div className="pdf-viewer-toolbar">
                                <div className="pdf-viewer-toolbar__group">
                                    <ZoomOut />
                                    <ZoomIn />
                                </div>
                                <div className="pdf-viewer-toolbar__group">
                                    <SwitchTheme />
                                    <EnterFullScreen />
                                    <Print />
                                    <Download />
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });
    const [scaledCoords, setScaledCoords] = useState([]);
    const [scaledSearchCoords, setScaledSearchCoords] = useState([]);
    const [viewportDimensions, setViewportDimensions] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!pdfUrl) {
            setViewportDimensions(null);
            setError(null);
        }
    }, [pdfUrl]);

    const scaleFactors = useMemo(() => {
        if (!imageSize || !viewportDimensions) {
            return null;
        }

        const { width: originalWidth, height: originalHeight } = imageSize;
        const { width: viewportWidth, height: viewportHeight } = viewportDimensions;

        if (!originalWidth || !originalHeight) {
            return null;
        }

        return {
            width: viewportWidth / originalWidth,
            height: viewportHeight / originalHeight,
        };
    }, [imageSize, viewportDimensions]);

    useEffect(() => {
        if (!scaleFactors) {
            setScaledCoords([]);
            setScaledSearchCoords([]);
            return;
        }

        setScaledCoords(
            scaleCoordinates(
                highlightCoords,
                scaleFactors.width,
                scaleFactors.height,
                false
            )
        );

        setScaledSearchCoords(
            scaleCoordinates(
                searchCoords,
                scaleFactors.width,
                scaleFactors.height,
                true
            )
        );
    }, [highlightCoords, searchCoords, scaleFactors]);

    const handleDocumentLoad = async (event) => {
        try {
            const page = await event.doc.getPage(1);
            const viewport = page.getViewport({ scale: 1.0 });
            setViewportDimensions({ width: viewport.width, height: viewport.height });
            setError(null);
        } catch (err) {
            const msg = String(err?.message || err);
            console.error('Error reading PDF dimensions:', err);
            setError(`Error loading PDF: ${msg}. Please check the URL or file structure.`);
            if (onDocumentLoadFail) {
                onDocumentLoadFail(err);
            }
            return;
        }

        if (onDocumentLoad) {
            onDocumentLoad(event);
        }
    };

    const handleDocumentLoadFail = (error) => {
        const msg = String(error?.message || error);
        setError(`Error loading PDF: ${msg}. Please check the URL or file structure.`);
        if (onDocumentLoadFail) {
            onDocumentLoadFail(error);
        }
    };

    const viewerStyles = {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div id="pdf-viewer" style={viewerStyles}>
            <Viewer
                key={pdfUrl}
                fileUrl={pdfUrl}
                plugins={[defaultLayoutPluginInstance]}
                renderPage={(props) => (
                    <>
                        {props.canvasLayer.children}
                        <AnnotationLayer
                            {...props}
                            highlightCoords={scaledCoords}
                            searchCoords={scaledSearchCoords}
                            selectedLine={selectedLine}
                        />
                    </>
                )}
                defaultScale={SpecialZoomLevel.PageFit}
                onDocumentLoad={handleDocumentLoad}
                onDocumentLoadFail={handleDocumentLoadFail}
            />
        </div>
    );
}
